import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import lodashGet from 'lodash/get';
import cx from 'classnames';
import { connect } from '../../../redux/connect';
import { openAttachmentAction, showAttachmentsInGallery } from '../../../redux/actions';
import downloadAttachmentThunk from '../../../redux/thunk/attachmentDownloadThunk';
import {
  getThumbnailUrlByAttachment,
  getOxygenMappings,
  getCurrentUser,
  getCurrentPreviewIssueAttributes,
} from '../../../redux/selectors';
import { deleteAttachmentAction } from '../../../redux/actions/attachment-actions';
import { FileItem, ActionButton, Text, ProgressBar, SvgIcon } from '@adsk/bim360-matrix-react-components';
import { shortRelativeDateString } from '../../../utils/date-helpers';
import { isImageAttachment } from '../../../utils/attachmentsHelper';
import { getUserNameDisplayNameWithCompany } from '../../../utils/userHelpers';
import { OSS_URN_TYPE, attachmentActions } from '../../../constants/attachment-types';
import { issueAttributesType } from '../../../constants/proptype-validation';
import T from 'i18n-react';
import { trimFileName } from '../../../utils/notificationHelpers';
import TryAgainErrorNotification from '../../TryAgainErrorNotification';
import { canDeleteAttachment } from '../../../utils/issues-helpers';

export class PureIssueAttachment extends PureComponent {

  constructor(props) {
    super(props);

    this.openAttachment = this.openAttachment.bind(this);
  }

  downloadAttachment() {
    const { attachment, downloadAttachment } = this.props;
    const trimmedFileName = trimFileName(attachment.name, 35);
    const failureMessage = <TryAgainErrorNotification message={T.translate('notifications.failedToDownload', { fileName: trimmedFileName })} tryAgainAction={() => this.downloadAttachment()} />;
    const asyncMessage = <Text hasHTML>{T.translate('notifications.preparingToDownload', { fileName: trimmedFileName })}</Text>;

    downloadAttachment(attachment, asyncMessage, failureMessage);
  }

  openAttachment() {
    const { attachment, openAttachmentAction, showAttachmentsInGallery } = this.props;
    const { urnType } = attachment;

    if (urnType === OSS_URN_TYPE) {
      if (isImageAttachment(attachment)) {
        showAttachmentsInGallery([attachment]);
      } else {
        this.downloadAttachment();
      }
    } else {
      openAttachmentAction(attachment);
    }
  }

  getCreatedBy() {
    const { attachment, oxygen } = this.props;
    return getUserNameDisplayNameWithCompany(oxygen, attachment.createdBy);
  }

  renderDescription() {
    const { attachment } = this.props;

    if (lodashGet(attachment, 'progress')) {
      const progressDescriptionText = attachment.isFailed ? T.translate('attachments.UploadFailed') : T.translate('attachments.uploading');
      const progressDescriptionClasses = cx('IssueAttachment__description', {
        'IssueAttachment__description--failed': attachment.isFailed,
      });

      return <div className={progressDescriptionClasses}>{progressDescriptionText}</div>;
    }

    return (
      <div className="IssueAttachment__description">
        <span className="IssueAttachment__created-by">{this.getCreatedBy()}</span>
        <span className="IssueAttachment__circle" />
        <span className="IssueAttachment__created-date">{shortRelativeDateString(attachment.createdAt)}</span>
      </div>
    );
  }

  renderThumbnail() {
    const { thumbnailUrl } = this.props;

    return (
      thumbnailUrl
        ? <img src={thumbnailUrl} className="IssueAttachment__thumbnail-img" data-testid="IssueAttachment__thumbnail-img" />
        : <SvgIcon width="24px" height="30px" className="IssueAttachment__thumbnail-icon" svgId="attachment-icon" />
    );
  }

  renderProgress() {
    const { attachment } = this.props;
    const progressToShow = lodashGet(attachment, 'progress', 0);

    const progressBarClasses = cx('IssueAttachment__progress', {
      'IssueAttachment__progress--upload-failed': attachment.isFailed,
    });

    return progressToShow ? <ProgressBar max={100} now={progressToShow} className={progressBarClasses} /> : null;
  }

  /**
   * handle attachment option selection
   */
  _handleAttachmentOptionSelection(eventKey) {
    const { attachment, issue, currentUser, deleteAttachmentAction } = this.props;
    switch (eventKey) {
      case attachmentActions.DELETE_ATTACHMENT: {
        const analyticsObj = {
          isOwner: attachment.createdBy === currentUser.attributes.id,
          status: issue.status,
          assignedTo: issue.assignedTo,
          assignedToType: issue.assignedToType,
        };
        deleteAttachmentAction(issue.id, attachment.id, analyticsObj);
        break;
      }
      default:
        break;
    }
  }

  /**
   * Render attachment options menu
   * @returns {XML}
   */
  renderOptionsButton() {
    const options = [];

    // Currently delete attachments is only supported in Field or in RFIs
    if (canDeleteAttachment(this.props.attachment)) {
      options.push({
        key: attachmentActions.DELETE_ATTACHMENT,
        label: T.translate('delete'),
        svgId: 'trash',
        width: '13px',
        height: '16px',
      });
    }

    if (options.length === 0) {
      return null;
    }

    return (
      <div className="IssueAttachment__actions" data-testid="IssueAttachment__actions">
        <ActionButton
          className="IssueAttachment__option-button"
          container={this.props.container}
          options={options}
          onSelect={(eventKey) => this._handleAttachmentOptionSelection(eventKey)}
        />
      </div>
    );
  }

  render() {
    const { attachment, className } = this.props;
    const isDeleting = lodashGet(attachment, 'isDeleting');
    const clickDisabled = lodashGet(attachment, 'isSaving') || lodashGet(attachment, 'isFailed') || isDeleting;
    const issueAttachmentClasses = cx('IssueAttachment', className, {
      'IssueAttachment--disabled': clickDisabled,
      'IssueAttachment--deleting': isDeleting,
    });

    if (this.props.attachmentComponent === PureIssueAttachment.attachmentComponents.FILE_ITEM) {
      return (
        <FileItem thumbnailUrl={this.props.thumbnailUrl} topBarRenderer={FileItem.clickableTopBar(this.openAttachment)}>
          <Text ellipsis>
            {attachment.name}
          </Text>
        </FileItem>
      );
    }

    return (
      <div className={issueAttachmentClasses} onClick={clickDisabled ? null : this.openAttachment}>
        <div className="IssueAttachment__top" data-testid="IssueAttachment__top">
          <div className="IssueAttachment__thumbnail">
            {this.renderThumbnail()}
          </div>
          <div className="IssueAttachment__info" data-testid="IssueAttachment__info">
            <Text ellipsis className="IssueAttachment__name" data-testid="IssueAttachment__name">
              {attachment.name}
            </Text>
            {this.renderDescription()}
          </div>
          {!clickDisabled
            ? this.renderOptionsButton()
            : null
          }
        </div>
        {this.renderProgress()}
      </div>
    );
  }
}

PureIssueAttachment.displayName = 'IssueAttachment';
PureIssueAttachment.attachmentComponents = {
  DEFAULT: 'default',
  FILE_ITEM: 'FileItem',
};

PureIssueAttachment.propTypes = {
  attachment: PropTypes.object,
  thumbnailUrl: PropTypes.string,
  oxygen: PropTypes.object.isRequired,
  openAttachmentAction: PropTypes.func.isRequired,
  showAttachmentsInGallery: PropTypes.func.isRequired,
  downloadAttachment: PropTypes.func.isRequired,
  container: PropTypes.object.isRequired,
  deleteAttachmentAction: PropTypes.func.isRequired,
  currentUser: PropTypes.object.isRequired,
  className: PropTypes.string,
  issue: issueAttributesType,
  attachmentComponent: PropTypes.string.isRequired,
};
PureIssueAttachment.defaultProps = {
  attachmentComponent: PureIssueAttachment.attachmentComponents.DEFAULT,
};

const mapStateToProps = (state, ownProps) => ({
  thumbnailUrl: getThumbnailUrlByAttachment(state, ownProps.attachment),
  oxygen: getOxygenMappings(state),
  currentUser: getCurrentUser(state),
  issue: getCurrentPreviewIssueAttributes(state),
});

const mapDispatchToProps = {
  openAttachmentAction,
  showAttachmentsInGallery,
  downloadAttachment: downloadAttachmentThunk.downloadAttachment,
  deleteAttachmentAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(PureIssueAttachment);
